import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const usernameRef = useRef(null);

  // If already logged in redirect straight to app
  useEffect(() => {
    if (user) navigate('/hub', { replace: true });
  }, [user, navigate]);

  // Read session_expired flag written by api.js on 401 — do NOT touch localStorage
  useEffect(() => {
    if (sessionStorage.getItem('session_expired') === '1') {
      setSessionExpired(true);
      sessionStorage.removeItem('session_expired');
    }
    setTimeout(() => usernameRef.current?.focus(), 50);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) {
      setError('Please enter both username and password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await login(form.username.trim(), form.password);
      navigate('/hub', { replace: true });
    } catch (err) {
      setError(err?.error || err?.message || 'Invalid username or password. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:'100vh',display:'flex',background:'var(--bg)',fontFamily:'inherit'}}>

      {/* ── Left branding panel */}
      <div style={{
        width:'400px',flexShrink:0,
        background:'linear-gradient(145deg,#1a2344 0%,#0d1526 100%)',
        display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
        padding:'48px',position:'relative',overflow:'hidden',
      }}>
        <div style={{position:'absolute',top:'-80px',left:'-80px',width:'320px',height:'320px',borderRadius:'50%',background:'rgba(91,91,214,0.08)',pointerEvents:'none'}}/>
        <div style={{position:'absolute',bottom:'-60px',right:'-60px',width:'240px',height:'240px',borderRadius:'50%',background:'rgba(91,91,214,0.06)',pointerEvents:'none'}}/>
        <div style={{textAlign:'center',position:'relative',width:'100%'}}>
          <div style={{width:'64px',height:'64px',borderRadius:'16px',background:'linear-gradient(135deg,#5B5BD6,#7C3AED)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'28px',fontWeight:'900',color:'#fff',margin:'0 auto 20px',boxShadow:'0 8px 32px rgba(91,91,214,0.4)'}}>E</div>
          <div style={{fontSize:'26px',fontWeight:'800',color:'#fff',letterSpacing:'-0.5px',marginBottom:'6px'}}>EOMP</div>
          <div style={{fontSize:'13px',color:'rgba(255,255,255,0.45)',marginBottom:'40px'}}>Enterprise Operations Management Platform</div>
          <div style={{fontSize:'11px',color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'12px',textAlign:'left'}}>One sign-in. All your applications.</div>
          <div style={{display:'flex',flexDirection:'column',gap:'12px',textAlign:'left'}}>
            {[['📁','Project Management — plans, tasks, financials & reports'],['🤝','CRM — accounts, pipeline, quotes & inventory'],['⚙️','Administration — users & application access']].map(([icon,text])=>(
              <div key={text} style={{display:'flex',alignItems:'center',gap:'12px',fontSize:'13px',color:'rgba(255,255,255,0.6)',padding:'8px 12px',borderRadius:'8px',background:'rgba(255,255,255,0.04)'}}>
                <span style={{fontSize:'16px',flexShrink:0}}>{icon}</span>{text}
              </div>
            ))}
          </div>
        </div>
        <div style={{position:'absolute',bottom:'24px',fontSize:'11px',color:'rgba(255,255,255,0.25)',textAlign:'center'}}>
          © 2026 Space Communication Technologies
        </div>
      </div>

      {/* ── Right login panel */}
      <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',padding:'40px',background:'var(--bg)'}}>
        <div style={{width:'100%',maxWidth:'400px'}}>
          <div style={{marginBottom:'32px'}}>
            <h2 style={{fontSize:'28px',fontWeight:'800',color:'var(--text)',letterSpacing:'-0.5px',margin:'0 0 6px'}}>Welcome back</h2>
            <p style={{color:'var(--muted)',fontSize:'14px',margin:0}}>Sign in to your SCT Omansat workspace</p>
          </div>

          {sessionExpired&&(
            <div style={{background:'rgba(251,191,36,0.1)',border:'1px solid rgba(251,191,36,0.35)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',display:'flex',alignItems:'flex-start',gap:'10px',fontSize:'13px',color:'#92400E'}}>
              <span style={{fontSize:'16px',flexShrink:0}}>⏱</span>
              <div><strong>Session expired.</strong> Please sign in again.</div>
            </div>
          )}

          {error&&(
            <div style={{background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:'10px',padding:'12px 16px',marginBottom:'20px',display:'flex',alignItems:'center',gap:'10px',fontSize:'13px',color:'#DC2626'}}>
              <span style={{fontSize:'16px',flexShrink:0}}>⚠️</span><span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div style={{marginBottom:'18px'}}>
              <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'var(--text)',marginBottom:'6px'}}>Username</label>
              <input ref={usernameRef} className="input" value={form.username}
                onChange={e=>{setForm({...form,username:e.target.value});setError('');}}
                placeholder="Enter your username" autoComplete="username" required
                style={{width:'100%',padding:'11px 14px',fontSize:'14px',borderRadius:'10px',boxSizing:'border-box',border:error?'1.5px solid #DC2626':'1.5px solid var(--border)',outline:'none',background:'var(--surface)',color:'var(--text)',transition:'border-color .15s'}}
                onFocus={e=>{if(!error)e.target.style.borderColor='#5B5BD6';}}
                onBlur={e=>{if(!error)e.target.style.borderColor='var(--border)';}}
              />
            </div>
            <div style={{marginBottom:'24px'}}>
              <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'var(--text)',marginBottom:'6px'}}>Password</label>
              <div style={{position:'relative'}}>
                <input className="input" type={showPass?'text':'password'} value={form.password}
                  onChange={e=>{setForm({...form,password:e.target.value});setError('');}}
                  placeholder="Enter your password" autoComplete="current-password" required
                  style={{width:'100%',padding:'11px 44px 11px 14px',fontSize:'14px',borderRadius:'10px',boxSizing:'border-box',border:error?'1.5px solid #DC2626':'1.5px solid var(--border)',outline:'none',background:'var(--surface)',color:'var(--text)',transition:'border-color .15s'}}
                  onFocus={e=>{if(!error)e.target.style.borderColor='#5B5BD6';}}
                  onBlur={e=>{if(!error)e.target.style.borderColor='var(--border)';}}
                />
                <button type="button" onClick={()=>setShowPass(v=>!v)} tabIndex={-1}
                  aria-label={showPass?'Hide password':'Show password'}
                  style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--muted)',padding:'2px',lineHeight:0,display:'flex',alignItems:'center'}}>
                  {showPass ? (
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4"/><path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4"/></svg>
                  )}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              style={{width:'100%',padding:'12px',fontSize:'14px',fontWeight:'700',borderRadius:'10px',border:'none',cursor:loading?'not-allowed':'pointer',background:loading?'#9CA3AF':'linear-gradient(135deg,#5B5BD6,#7C3AED)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',transition:'all .2s',boxShadow:loading?'none':'0 4px 14px rgba(91,91,214,0.35)'}}>
              {loading?(<><div style={{width:14,height:14,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>Signing in…</>):'Sign in →'}
            </button>
            <div style={{textAlign:'right',marginTop:'10px'}}>
              <a href="/forgot-password" style={{fontSize:'12px',color:'var(--muted)',textDecoration:'none'}}
                onMouseEnter={e=>e.target.style.color='#5B5BD6'}
                onMouseLeave={e=>e.target.style.color='var(--muted)'}>
                Forgot password?
              </a>
            </div>
          </form>


          <div style={{textAlign:'center',marginTop:'28px',paddingTop:'20px',borderTop:'1px solid var(--border)',color:'var(--muted)',fontSize:'12px',lineHeight:'1.5'}}>
            © 2026 Space Communication Technologies — OmanSat<br/>
            <span style={{opacity:0.6}}>Enterprise Operations Management Platform</span>
          </div>
        </div>
      </div>
    </div>
  );
}
